import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Eye, Edit2, AlertCircle } from 'lucide-react';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { FormInput } from '../components/common/FormInput';
import { Pagination } from '../components/common/Pagination';
import { getStocks, createStock, deleteStock } from '../services/stocks';

export const StocksPage = () => {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState([]);
  const [filters, setFilters] = useState({ search: '', category: '', date: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    quantity: '',
    unit_price: '',
  });
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stockToDelete, setStockToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Load stocks from API
  useEffect(() => {
    const fetchStocks = async () => {
      try {
        setLoading(true);
        const data = await getStocks();
        
        const stockList = Array.isArray(data) ? data : (data.stocks || data.results || data || []);
        
        setStocks(Array.isArray(stockList) ? stockList : []);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load stocks');
        setStocks([]);
      } finally {
        setLoading(false);
      }
    };
    fetchStocks();
  }, []);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddStock = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!formData.name) newErrors.name = 'Product name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.quantity) newErrors.quantity = 'Quantity is required';
    if (!formData.unit_price) newErrors.unit_price = 'Unit price is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const payload = {
        name: formData.name,
        category: formData.category,
        quantity: parseInt(formData.quantity),
        unit_price: formData.unit_price,
      };

      const newStock = await createStock(payload);
      setStocks([...stocks, newStock]);

      setSuccess(true);
      setFormData({
        name: '',
        category: '',
        quantity: '',
        unit_price: '',
      });

      setTimeout(() => {
        setShowAddForm(false);
        setSuccess(false);
      }, 1500);
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to add stock' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewStock = (stock) => {
    navigate(`/stocks/${stock.id}/view`, { state: { stock } });
  };

  const handleEditStock = (stock) => {
    navigate(`/stocks/${stock.id}/edit`, { state: { stock } });
  };

  const handleDeleteStock = (id) => {
    setStockToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteStock = async () => {
    if (!stockToDelete) return;
    try {
      await deleteStock(stockToDelete);
      setStocks(stocks.filter(stock => stock.id !== stockToDelete));
      setShowDeleteConfirm(false);
      setStockToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete stock');
      setShowDeleteConfirm(false);
      setStockToDelete(null);
    }
  };

  const filteredStocks = stocks.filter(stock => {
    // Handle missing stock or fields
    if (!stock) return false;
    
    const stockName = stock.name || '';
    const stockCategory = stock.category || '';
    
    const matchesSearch = stockName.toLowerCase().includes(filters.search.toLowerCase());
    const matchesCategory = !filters.category || stockCategory === filters.category;
    
    // Filter by date (created_at field)
    let matchesDate = true;
    if (filters.date) {
      const filterDate = filters.date; // Format: YYYY-MM-DD
      if (stock.created_at) {
        const stockDate = stock.created_at.includes('T') 
          ? stock.created_at.split('T')[0] 
          : stock.created_at;
        matchesDate = stockDate === filterDate;
      } else {
        matchesDate = false;
      }
    }
    
    return matchesSearch && matchesCategory && matchesDate;
  });

  const totalPages = Math.ceil(filteredStocks.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedStocks = filteredStocks.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  if (showAddForm) {
    return (
      <AuthenticatedLayout>
        <div className="space-y-6 px-4 sm:px-0">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Add Stock Item</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Add a new product to your inventory</p>
          </div>

          <Card>
            <form onSubmit={handleAddStock} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label="Product Name"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  error={errors.name}
                  required
                />
                <FormInput
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleFormChange}
                  error={errors.category}
                  placeholder="e.g., Electronics, Clothing, etc."
                  required
                />
                <FormInput
                  label="Quantity"
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleFormChange}
                  error={errors.quantity}
                  required
                />
                <FormInput
                  label="Unit Price"
                  type="number"
                  name="unit_price"
                  value={formData.unit_price}
                  onChange={handleFormChange}
                  error={errors.unit_price}
                  step="0.01"
                  required
                />
              </div>

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-red-800">{errors.submit}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
                  Stock item added successfully!
                </div>
              )}

              <div className="flex gap-4">
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? 'Adding...' : 'Add Stock Item'}
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <div className="space-y-6 px-4 sm:px-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Stock Management</h1>
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">Manage and track your product inventory</p>
          </div>
          <Button 
            variant="primary" 
            onClick={() => setShowAddForm(true)}
            className="w-full sm:w-auto"
            disabled={loading}
          >
            <Plus className="w-5 h-5 mr-2 inline" />
            <span className="hidden sm:inline">Add Stock</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>

        {/* Loading State */}
        {loading && (
          <Card>
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Loading stocks...</p>
              </div>
            </div>
          </Card>
        )}

        {/* Error State */}
        {error && !loading && (
          <Card>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-red-800">{error}</p>
            </div>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput
              type="text"
              name="search"
              placeholder="Search by product name"
              value={filters.search}
              onChange={handleFilterChange}
            />
            <FormInput
              type="text"
              name="category"
              placeholder="Filter by category"
              value={filters.category}
              onChange={handleFilterChange}
            />
            <FormInput
              type="date"
              name="date"
              value={filters.date}
              onChange={handleFilterChange}
            />
          </div>
        </Card>

        {/* Stocks Table */}
        <Card>
          {filteredStocks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg">No stock items found</p>
              <Button 
                variant="primary" 
                onClick={() => setShowAddForm(true)}
                className="mt-4"
              >
                <Plus className="w-5 h-5 mr-2 inline" />
                Add First Stock Item
              </Button>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
                    <tr>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">Product Name</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">Category</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">Quantity</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">Unit Price</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">Total Value</th>
                      <th className="px-3 sm:px-6 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-gray-600">
                    {paginatedStocks.map(stock => {
                      const quantity = parseInt(stock.quantity) || 0;
                      const unitPrice = parseFloat(stock.unit_price) || 0;
                      const totalValue = quantity * unitPrice;
                      return (
                        <tr key={stock.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 dark:text-white font-medium">{stock.name}</td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-700 dark:text-gray-300">{stock.category}</td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right text-gray-900 dark:text-white font-medium">{quantity}</td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right text-gray-700 dark:text-gray-300">₦{unitPrice.toFixed(2)}</td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-right text-gray-900 dark:text-white font-medium">₦{totalValue.toLocaleString()}</td>
                          <td className="px-3 sm:px-6 py-2 sm:py-4 text-center">
                            <div className="flex items-center justify-center gap-1 sm:gap-2">
                              <button
                                onClick={() => handleViewStock(stock)}
                                className="inline-flex items-center justify-center p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition"
                                title="View stock"
                              >
                                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                              <button
                                onClick={() => handleEditStock(stock)}
                                className="inline-flex items-center justify-center p-1.5 sm:p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition"
                                title="Edit stock"
                              >
                                <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteStock(stock.id)}
                                className="inline-flex items-center justify-center p-1.5 sm:p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                                title="Delete stock"
                              >
                                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="sm:hidden space-y-3">
                {paginatedStocks.map(stock => {
                  const quantity = parseInt(stock.quantity) || 0;
                  const unitPrice = parseFloat(stock.unit_price) || 0;
                  const totalValue = quantity * unitPrice;
                  return (
                    <div key={stock.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-900 dark:text-white truncate">{stock.name}</p>
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded font-semibold inline-block mt-1">
                            {stock.category}
                          </span>
                        </div>
                        <p className="text-sm font-bold text-gray-900 dark:text-white ml-2 whitespace-nowrap">₦{totalValue.toLocaleString()}</p>
                      </div>
                      <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400 mb-3">
                        <span>Qty: {quantity}</span>
                        <span>Unit: ₦{unitPrice.toFixed(2)}</span>
                      </div>
                      <div className="flex gap-2 border-t dark:border-gray-600 pt-2">
                        <button
                          onClick={() => handleViewStock(stock)}
                          className="flex-1 p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded text-xs flex items-center justify-center gap-1 transition font-semibold"
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                        <button
                          onClick={() => handleEditStock(stock)}
                          className="flex-1 p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded text-xs flex items-center justify-center gap-1 transition font-semibold"
                        >
                          <Edit2 className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button
                          onClick={() => handleDeleteStock(stock.id)}
                          className="flex-1 p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded text-xs flex items-center justify-center gap-1 transition font-semibold"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <Pagination 
                  currentPage={currentPage} 
                  totalPages={totalPages} 
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </Card>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="max-w-md">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Delete Stock</h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Are you sure you want to delete this stock item? This action cannot be undone.
            </p>
            <div className="flex gap-4">
              <Button 
                variant="danger" 
                onClick={confirmDeleteStock}
                className="flex-1"
              >
                Delete
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setStockToDelete(null);
                }}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </div>
      )}
    </AuthenticatedLayout>
  );
};
